import { useState } from 'react'
import Navbar from './Navbar'
import StatsCards from './StatsCards'
import DevicesList, { type SortField, type SortOrder, type StatusFilterValue } from './DevicesList'
import LogsList from './LogsList'
import ControlPanel from './ControlPanel'
import MasterDataTabs from './MasterDataTabs'
import UserManagement from './UserManagement'
import QrCodeGenerator from './QrCodeGenerator'
import { useAuth } from '../contexts/AuthContext'

type Tab = 'devices' | 'logs' | 'control' | 'master' | 'users' | 'qr'

// Helper to check if user is admin
const isAdmin = (user: { role: string } | null): boolean => user?.role === 'admin'

interface ControlPanelData {
  deviceId: string
  endpoint: string
}

export default function Dashboard() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('devices')
  const [controlPanelData, setControlPanelData] = useState<ControlPanelData | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all')
  const [sortField, setSortField] = useState<SortField>('mago_id')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [currentPage, setCurrentPage] = useState(1)

  const handleSendMessage = (deviceId: string, endpoint: string) => {
    console.log('Dashboard: handleSendMessage called with:', { deviceId, endpoint })
    setControlPanelData({ deviceId, endpoint })
    setActiveTab('control')
    console.log('Dashboard: switched to control tab with data:', { deviceId, endpoint })
  }

  const devicesList = (
    <DevicesList
      onSendMessage={handleSendMessage}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      sortField={sortField}
      setSortField={setSortField}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
    />
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'devices':
        return devicesList
      case 'logs':
        return <LogsList />
      case 'control':
        return (
          <ControlPanel
            initialData={controlPanelData}
            onDataUsed={() => setControlPanelData(null)}
          />
        )
      case 'master':
        return <MasterDataTabs />
      case 'users':
        return <UserManagement />
      case 'qr':
        return <QrCodeGenerator />
      default:
        return devicesList
    }
  }

  return (
    <>
      <Navbar />

      <div className="container mt-4">
        <StatsCards />

        <div className="card">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'devices' ? 'active' : ''}`}
                  onClick={() => setActiveTab('devices')}
                  type="button"
                  role="tab"
                >
                  <i className="fas fa-microchip me-2" />
                  端末
                </button>
              </li>
              {isAdmin(user) && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'control' ? 'active' : ''}`}
                    onClick={() => setActiveTab('control')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-play me-2" />
                    Control
                  </button>
                </li>
              )}
              {isAdmin(user) && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'master' ? 'active' : ''}`}
                    onClick={() => setActiveTab('master')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-database me-2" />
                    マスターデータ
                  </button>
                </li>
              )}
              {isAdmin(user) && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('logs')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-list me-2" />
                    Logs
                  </button>
                </li>
              )}
              {isAdmin(user) && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-users me-2" />
                    ユーザー
                  </button>
                </li>
              )}
              {isAdmin(user) && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'qr' ? 'active' : ''}`}
                    onClick={() => setActiveTab('qr')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-qrcode me-2" />
                    QRコード
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div className="card-body">
            <div className="tab-content">
              <div className="tab-pane fade show active" role="tabpanel">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
